<template>
    <section class="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-3" aria-label="Recorded account evidence">
        <template v-if="evidence.status === 'found'">
            <div>
                <h3 class="font-semibold text-slate-900">Recorded evidence: {{ evidence.account.name || evidence.account.domain }}</h3>
                <div class="text-slate-500">{{ evidence.account.domain }} · Account {{ evidence.account.id }} · Observed {{ evidence.observedAt }}</div>
            </div>
            <p class="rounded bg-amber-50 p-2 text-amber-900"><strong>Original admission source: not recorded.</strong> Current list membership and research do not prove why this account originally entered.</p>
            <p>{{ evidence.membership ? 'A current campaign account record exists.' : 'No current campaign account record.' }}</p>
            <div>
                <h4 class="font-medium">Attached list memberships</h4>
                <ul v-if="evidence.sources.length" class="list-disc pl-4 space-y-1">
                    <li v-for="source in evidence.sources" :key="`${source.kind}-${source.sourceRowId}`">
                        {{ source.sourceListName }} · {{ source.kind }} row {{ source.sourceRowNumber ?? source.sourceRowId }} · {{ source.origin }}
                    </li>
                </ul>
                <p v-else class="text-slate-500">No matching active rows in attached lists. This does not establish database discovery.</p>
                <p v-if="evidence.sourcesTruncated" class="text-slate-500">Showing the first 50 source rows.</p>
            </div>
            <div>
                <h4 class="font-medium">Recorded research</h4>
                <p class="text-slate-500">Individual signal results, not a historical admission decision. No match is not proof of absence.</p>
                <div v-for="signal in evidence.signals" :key="signal.signalId" class="border-t border-slate-100 py-2">
                    <p><strong>{{ signal.name }}</strong>: {{ signalLabel(signal) }}</p>
                    <p v-if="signal.checkedAt" class="text-slate-500">Checked {{ signal.checkedAt }} · Result {{ signal.resultId }}</p>
                    <p v-if="signal.reasoning" class="whitespace-pre-wrap mt-1">{{ signal.reasoning }}</p>
                    <details v-if="signal.evidence" class="mt-1">
                        <summary class="cursor-pointer text-blue-700">Recorded supporting evidence</summary>
                        <p class="whitespace-pre-wrap break-words text-slate-600">{{ signal.evidence }}</p>
                    </details>
                </div>
                <p v-if="!evidence.signals.length" class="text-slate-500">No configured research signals.</p>
            </div>
            <div v-if="evidence.contacts.length">
                <h4 class="font-medium">Campaign contacts (up to 25)</h4>
                <p v-for="contact in evidence.contacts" :key="contact.assignmentId ?? contact.id">
                    {{ contact.name || 'Name not recorded' }} · {{ contact.email || 'Email not recorded' }} · Assignment {{ contact.assignmentId }}
                </p>
            </div>
        </template>
        <template v-else>
            <p>{{ evidence.message }}</p>
            <p v-for="account in evidence.candidates" :key="account.id">{{ account.name || account.domain }} · {{ account.domain }} · ID {{ account.id }}</p>
        </template>
    </section>
</template>

<script setup lang="ts">
import type { AccountEvidence, AccountEvidenceResult } from '@coldreach/agent';

defineProps<{ evidence: AccountEvidenceResult }>();
function signalLabel(signal: AccountEvidence['signals'][number]) {
    if (!signal.resultId) return 'Not researched';
    if (signal.status !== 'completed') return `Not completed (${signal.status})`;
    if (!signal.current) return 'Outdated for the current rule';
    return signal.qualified ? 'Matching evidence found' : 'No matching evidence found';
}
</script>
